/**
 * @license
 * Copyright 2022 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// A TYPE-only namespace: every `Aglyn.` reference below is a type, and held
// as a VALUE the namespace pins every module the barrel reaches into the
// console shell that renders these hooks. `import type` erases instead.
import type * as Aglyn from '@aglyn/aglyn'
import {
  doc,
  type DocumentSnapshot,
  type SnapshotOptions,
} from 'firebase/firestore'
import { useFirestore, type FirestoreDocOptions } from './firebase/firebase-services'
import useDoc from './helpers/use-doc'

export const useHostRef = ({ hostId }: { hostId: Aglyn.HostUid }) => {
  const firestore = useFirestore()
  const ref = doc(firestore, 'hosts', hostId)
  return ref.withConverter({
    toFirestore(data: Aglyn.AglynHost) {
      const { $id, ...rest } = data
      return rest
    },
    fromFirestore(
      snapshot: DocumentSnapshot<Aglyn.AglynHost>,
      options: SnapshotOptions,
    ) {
      if (!snapshot.exists()) return undefined
      const data = snapshot.data(options)
      return data as Aglyn.AglynHost
    },
  })
}

export const useHost = (
  data: { hostId: Aglyn.HostUid },
  options?: FirestoreDocOptions<Aglyn.AglynHost>,
) => {
  return useDoc(useHostRef(data), options)
}

export default useHost
